import * as types from './types';
import { getErrorMessage, parsePagination } from 'utils';
import { uiActions } from 'state/modules/UI';
import * as AccessDeviceAPI from 'services/api/accessDevice';
import i18n from 'i18n-js';

// Get AccessDevices list
const fetchAccessDevicesRequest = () => ({
  type: types.FETCH_ACCESS_DEVICE_REQUEST,
});

const fetchAccessDevicesSuccess = (payload) => ({
  type: types.FETCH_ACCESS_DEVICE_SUCCESS,
  payload,
});

const fetchAccessDevicesFailed = (error) => ({
  type: types.FETCH_ACCESS_DEVICE_FAILED,
  error,
});

export const fetchAccessDevices = (params = {}) => {
  return async (dispatch) => {
    dispatch(fetchAccessDevicesRequest());

    const response = await AccessDeviceAPI.fetchAccessDevices(params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchAccessDevicesSuccess({
          pagination: pagination,
          data: response.data,
        })
      );
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchAccessDevicesFailed());
  };
};

// Get AccessDevice by ID
const fetchAccessDeviceRequest = () => ({
  type: types.FETCH_ACCESS_DEVICE_BY_ID_REQUEST,
});

const fetchAccessDeviceSuccess = (payload) => ({
  type: types.FETCH_ACCESS_DEVICE_BY_ID_SUCCESS,
  payload,
});

const fetchAccessDeviceFailed = (error) => ({
  type: types.FETCH_ACCESS_DEVICE_BY_ID_FAILED,
  error,
});

export const fetchAccessDeviceById = (id) => {
  return async (dispatch) => {
    dispatch(fetchAccessDeviceRequest());

    const response = await AccessDeviceAPI.fetchAccessDeviceById(id);

    if (response.ok) {
      return dispatch(fetchAccessDeviceSuccess({ data: response.data }));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchAccessDeviceFailed(error));
  };
};

export const fetchAccessDeviceByUserId = (id) => {
  return async (dispatch) => {
    dispatch(fetchAccessDeviceRequest());

    const response = await AccessDeviceAPI.fetchAccessDevices({ user_id: id });

    if (response.ok) {
      return dispatch(fetchAccessDeviceSuccess({ data: response.data[0] }));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchAccessDeviceFailed(error));
  };
};

// Create AccessDevice
const createAccessDeviceRequest = () => ({
  type: types.CREATE_ACCESS_DEVICE_REQUEST,
});

const createAccessDeviceSuccess = (payload) => ({
  type: types.CREATE_ACCESS_DEVICE_SUCCESS,
  payload,
});

const createAccessDeviceFailed = (errors) => ({
  type: types.CREATE_ACCESS_DEVICE_FAILED,
  errors,
});

export const createAccessDevice = (payload) => {
  return async (dispatch) => {
    dispatch(createAccessDeviceRequest());

    const response = await AccessDeviceAPI.createAccessDevice({
      ...payload,
      device_id: payload.device_id.toUpperCase(),
    });

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('nfc.access_device_registered'),
        })
      );
      return dispatch(createAccessDeviceSuccess({ data: response.data }));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(createAccessDeviceFailed(response.data));
  };
};

// Get AccessDevice by ID
const updateAccessDeviceRequest = () => ({
  type: types.UPDATE_ACCESS_DEVICE_REQUEST,
});

const updateAccessDeviceSuccess = (payload) => ({
  type: types.UPDATE_ACCESS_DEVICE_SUCCESS,
  payload,
});

const updateAccessDeviceFailed = (errors) => ({
  type: types.UPDATE_ACCESS_DEVICE_FAILED,
  errors,
});

export const updateAccessDevice = (id, payload) => {
  return async (dispatch) => {
    dispatch(updateAccessDeviceRequest());

    const response = await AccessDeviceAPI.updateAccessDevice(id, payload);

    if (response.ok) {
      return dispatch(updateAccessDeviceSuccess({ data: response.data }));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(updateAccessDeviceFailed(response.data));
  };
};

// Delete AccessDevice by ID
const deleteAccessDeviceRequest = () => ({
  type: types.DELETE_ACCESS_DEVICE_REQUEST,
});

const deleteAccessDeviceSuccess = () => ({
  type: types.DELETE_ACCESS_DEVICE_SUCCESS,
});

const deleteAccessDeviceFailed = (errors) => ({
  type: types.DELETE_ACCESS_DEVICE_FAILED,
  errors,
});

export const deleteAccessDevice = (id) => {
  return async (dispatch) => {
    dispatch(deleteAccessDeviceRequest());

    const response = await AccessDeviceAPI.deleteAccessDevice(id);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('nfc.access_device_archived'),
        })
      );
      return dispatch(deleteAccessDeviceSuccess());
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(deleteAccessDeviceFailed(error));
  };
};

// Assign Access Device
const assignAccessDeviceRequest = () => ({
  type: types.ASSIGN_ACCESS_DEVICE_REQUEST,
});

const assignAccessDeviceSuccess = (payload) => ({
  type: types.ASSIGN_ACCESS_DEVICE_SUCCESS,
  payload,
});

const assignAccessDeviceFailed = (errors) => ({
  type: types.ASSIGN_ACCESS_DEVICE_FAILED,
  errors,
});

export const assignAccessDevice = (payload) => {
  return async (dispatch) => {
    dispatch(assignAccessDeviceRequest());

    const response = await AccessDeviceAPI.assignAccessDevice(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('nfc.access_device_assigned'),
        })
      );
      return dispatch(assignAccessDeviceSuccess({ data: response.data }));
    }

    const message = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message,
      })
    );

    dispatch(assignAccessDeviceFailed(message));
  };
};

// Unassign Access Device
const unassignAccessDeviceRequest = () => ({
  type: types.UNASSIGN_ACCESS_DEVICE_REQUEST,
});

const unassignAccessDeviceSuccess = (payload) => ({
  type: types.UNASSIGN_ACCESS_DEVICE_SUCCESS,
  payload,
});

const unassignAccessDeviceFailed = (errors) => ({
  type: types.UNASSIGN_ACCESS_DEVICE_FAILED,
  errors,
});

export const unassignAccessDevice = (payload) => {
  return async (dispatch) => {
    dispatch(unassignAccessDeviceRequest());

    const response = await AccessDeviceAPI.unassignAccessDevice(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('nfc.access_device_unassigned'),
        })
      );
      return dispatch(unassignAccessDeviceSuccess({ data: response.data }));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(unassignAccessDeviceFailed(error));
  };
};

// Reset AccessDevice State
export const resetAccessDeviceState = () => {
  return async (dispatch) => {
    dispatch({
      type: types.RESET_ACCESS_DEVICE_STATE,
    });
  };
};

// Reset AccessDevice
export const resetAccessDevice = () => {
  return async (dispatch) => {
    dispatch({
      type: types.RESET_ACCESS_DEVICE,
    });
  };
};

const fetchAddBrokerRequest = (payload) => ({
  type: types.FETCH_ADD_BROKER_REQUEST,
  payload,
});

const fetchAddBrokerSuccess = (payload) => ({
  type: types.FETCH_ADD_BROKER_SUCCESS,
  payload,
});

const fetchAddBrokerFailed = (payload) => ({
  type: types.FETCH_ADD_BROKER_FAILED,
  payload,
});

export const fetchAddBroker = (id, payload) => async (dispatch) => {
  dispatch(fetchAddBrokerRequest());

  const response = await AccessDeviceAPI.addBrokerToAccessDevice(id, payload);

  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('brokers.added_successfully'),
      })
    );
    dispatch(fetchAddBrokerSuccess(response.data));
    return await dispatch(fetchAccessDeviceById(id));
  }

  const formattedError = getErrorMessage(response.data);
  const errorsDetails = response?.data?.details || [];
  const errors = {};

  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: formattedError,
    })
  );

  errorsDetails?.length > 0 &&
    errorsDetails?.map((val) => {
      return (errors[val.field] = val.message);
    });

  dispatch(fetchAddBrokerFailed({ errors }));
};

const fetchRemoveBrokerRequest = (payload) => ({
  type: types.FETCH_REMOVE_BROKER_REQUEST,
  payload,
});

const fetchRemoveBrokerSuccess = (payload) => ({
  type: types.FETCH_REMOVE_BROKER_SUCCESS,
  payload,
});

const fetchRemoveBrokerFailed = (payload) => ({
  type: types.FETCH_REMOVE_BROKER_FAILED,
  payload,
});

export const fetchRemoveBroker =
  (id, broker, step = 'connection') =>
  async (dispatch) => {
    dispatch(fetchRemoveBrokerRequest());

    const response = await AccessDeviceAPI.removeBrokerFromAccessDevice(id, broker);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: 'Broker removed successfully',
        })
      );
      dispatch(
        fetchRemoveBrokerSuccess({
          step,
          data: response.data,
        })
      );
      return await dispatch(fetchAccessDeviceById(id));
    }

    const formattedError = getErrorMessage(response.data);

    if (typeof formattedError === 'string') {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(fetchRemoveBrokerFailed({ errors: formattedError }));
  };
